import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CopyButton } from "@/components/ui/copy-button";
import { Spinner } from "@/components/ui/spinner";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime, formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  WrenchIcon,
  BotIcon,
  UserIcon,
} from "lucide-react";

type Detail = components["schemas"]["AdminConversationDetailResponse"];
type Message = components["schemas"]["AdminAgentMessageResponse"];
type Role = components["schemas"]["AgentMessageRole"];

const roleMeta: Record<Role, { label: string; icon: React.ComponentType<{ className?: string }>; className: string }> = {
  User: {
    label: "User",
    icon: UserIcon,
    className: "border-l-4 border-l-blue-500 bg-blue-500/5",
  },
  Assistant: {
    label: "Assistant",
    icon: BotIcon,
    className: "border-l-4 border-l-emerald-500 bg-emerald-500/5",
  },
  Tool: {
    label: "Tool",
    icon: WrenchIcon,
    className: "border-l-4 border-l-amber-500 bg-amber-500/5",
  },
};

function prettifyJson(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function AgentConversationPage() {
  const { conversationId } = useParams<{ conversationId: string }>();

  const [detail, setDetail] = useState<Detail | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState<string | null>(null);

  const pagination = useCursorPagination({ initialLimit: 50 });
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [messagesError, setMessagesError] = useState<string | null>(null);

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;

    async function fetchDetail() {
      setDetailLoading(true);
      setDetailError(null);
      const { data, error } = await api.GET("/admin/agent/conversations/{conversationId}", {
        params: { path: { conversationId: conversationId! } },
      });
      if (cancelled) return;
      if (error) {
        setDetailError("Failed to load conversation.");
      } else {
        setDetail(data ?? null);
      }
      setDetailLoading(false);
    }

    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;

    async function fetchMessages() {
      setMessagesLoading(true);
      setMessagesError(null);
      const { data, error } = await api.GET(
        "/admin/agent/conversations/{conversationId}/messages",
        {
          params: {
            path: { conversationId: conversationId! },
            query: {
              Cursor: pagination.cursor,
              Limit: pagination.limit,
            },
          },
        },
      );
      if (cancelled) return;
      if (error) {
        setMessagesError("Failed to load messages.");
      } else {
        setMessages(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setMessagesLoading(false);
    }

    fetchMessages();
    return () => {
      cancelled = true;
    };
  }, [conversationId, pagination.cursor, pagination.limit]);

  if (detailError) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{detailError}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">
          {detail?.title || "Agent Conversation"}
        </h1>
        {conversationId && (
          <div className="mt-2 flex items-center gap-1">
            <Badge variant="secondary" className="font-mono text-xs">
              {conversationId}
            </Badge>
            <CopyButton value={conversationId} />
          </div>
        )}
      </div>

      {detailLoading ? (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      ) : detail ? (
        <DetailCard detail={detail} />
      ) : null}

      <div className="mt-8">
        <h2 className="mb-4 text-lg font-semibold">
          Messages
          {detail?.messageCount != null && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({formatNumber(detail.messageCount)})
            </span>
          )}
        </h2>

        {messagesError && <p className="text-destructive">{messagesError}</p>}

        <div className="space-y-3">
          {messages.length === 0 && !messagesLoading ? (
            <p className="text-muted-foreground py-8 text-center">No messages.</p>
          ) : (
            messages.map((m) => <MessageCard key={m.id} message={m} />)
          )}
        </div>

        {messagesLoading && (
          <div className="flex justify-center py-4">
            <Spinner className="size-5" />
          </div>
        )}

        <PaginationBar
          hasPrevious={pagination.hasPrevious}
          hasNext={pagination.hasMore}
          isLoading={messagesLoading}
          limit={pagination.limit}
          onPrevious={pagination.goPrevious}
          onNext={pagination.goNext}
          onLimitChange={pagination.setLimit}
        />
      </div>
    </div>
  );
}

function DetailCard({ detail }: { detail: Detail }) {
  const [promptOpen, setPromptOpen] = useState(false);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Overview</CardTitle>
        {detail.agentType && <Badge variant="outline">{detail.agentType}</Badge>}
      </CardHeader>
      <CardContent className="space-y-6">
        <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Organisation">
            {detail.organisationId ? (
              <Link
                to={`/organisations/${detail.organisationId}`}
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                <Badge variant="secondary" className="font-mono text-xs">
                  {detail.organisationId.slice(0, 8)}…
                </Badge>
                <ExternalLinkIcon className="size-3" />
              </Link>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </Field>
          <Field label="User">
            {detail.userId ? (
              <Link
                to={`/users/${detail.userId}`}
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                <Badge variant="secondary" className="font-mono text-xs">
                  {detail.userId.slice(0, 8)}…
                </Badge>
                <ExternalLinkIcon className="size-3" />
              </Link>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </Field>
          <Field label="Agent Surface">
            <span className="font-mono text-xs">{detail.agentSurface}</span>
          </Field>
          <Field label="Messages">
            <span className="tabular-nums">{formatNumber(detail.messageCount)}</span>
          </Field>
          <Field label="Truncated">
            <span className="tabular-nums">{formatNumber(detail.truncatedMessageCount)}</span>
          </Field>
          <Field label="Tokens (in / out)">
            <span className="tabular-nums text-sm">
              {formatNumber(detail.totalInputTokens)} / {formatNumber(detail.totalOutputTokens)}
            </span>
          </Field>
          <Field label="First Message">
            <span className="text-sm">{formatDateTime(detail.firstMessageAt)}</span>
          </Field>
          <Field label="Last Message">
            <span className="text-sm">{formatDateTime(detail.lastMessageAt)}</span>
          </Field>
          <Field label="Created">
            <span className="text-sm">{formatDateTime(detail.createdAt)}</span>
          </Field>
        </dl>

        {detail.availableTools && detail.availableTools.length > 0 && (
          <div>
            <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase mb-2">
              Available Tools ({detail.availableTools.length})
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {detail.availableTools.map((tool) => (
                <Badge key={tool} variant="outline" className="font-mono text-xs">
                  {tool}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {detail.systemPrompt && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPromptOpen((v) => !v)}
              className="mb-2 -ml-2 h-auto p-2"
            >
              {promptOpen ? (
                <ChevronDownIcon className="size-4" />
              ) : (
                <ChevronRightIcon className="size-4" />
              )}
              <span className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
                System Prompt
              </span>
            </Button>
            {promptOpen && (
              <pre className="max-h-[60vh] overflow-auto rounded-md border bg-muted/30 p-3 text-xs whitespace-pre-wrap font-mono">
                {detail.systemPrompt}
              </pre>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  );
}

function MessageCard({ message }: { message: Message }) {
  const role: Role = message.role ?? "User";
  const meta = roleMeta[role] ?? roleMeta.User;
  const Icon = meta.icon;

  const toolCalls = prettifyJson(message.toolCallsJson);
  const toolResults = prettifyJson(message.toolResultsJson);

  return (
    <div className={cn("rounded-md border p-4", meta.className)}>
      <div className="mb-2 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 font-semibold">
          <Icon className="size-4" />
          <span>{meta.label}</span>
          {message.truncated && (
            <Badge variant="destructive" className="text-xs">
              truncated
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 text-muted-foreground tabular-nums">
          {(message.inputTokens != null || message.outputTokens != null) && (
            <span>
              {formatNumber(message.inputTokens)} in · {formatNumber(message.outputTokens)} out
            </span>
          )}
          <span>{formatDateTime(message.createdAt)}</span>
        </div>
      </div>

      {message.content && (
        <div className="whitespace-pre-wrap text-sm leading-relaxed">
          {message.content}
        </div>
      )}

      {toolCalls && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-semibold text-muted-foreground hover:text-foreground">
            Tool calls
          </summary>
          <pre className="mt-2 max-h-96 overflow-auto rounded-md border bg-muted/50 p-3 text-xs font-mono">
            {toolCalls}
          </pre>
        </details>
      )}

      {toolResults && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-semibold text-muted-foreground hover:text-foreground">
            Tool results
          </summary>
          <pre className="mt-2 max-h-96 overflow-auto rounded-md border bg-muted/50 p-3 text-xs font-mono">
            {toolResults}
          </pre>
        </details>
      )}
    </div>
  );
}
